import { IContentCalloutTemplate } from '@dadohr/shared/domainTypes/experience';
import { PAEventTypeV2 } from '@dadohr/shared/domainTypes/productAnalytics';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Popover } from '@material-ui/core';
import {
  commands as mdCommands,
  ContextStore,
  ICommand,
  TextAreaTextApi,
  TextState
} from '@uiw/react-md-editor';
import { AlignCenter, AlignLeft, AlignRight } from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GradientButtonWithPromise } from '../../../../../../components/ButtonWithPromise/GradientButtonWithPromise';

import {
  MarkdownEditor,
  TokenDefinition,
  TokenSelectorDialog
} from '../../../../../../components/MarkdownText';
import { TokenPickerPopover } from '../../../../../../components/TextFieldWithReplacementTokens';
import { useSnackbar } from '../../../../../../hooks/useSnackbar';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { useIsOrgAIEnabled } from '../../../../../../services/orgSettings';
import { useProductAnalytics } from '../../../../../../services/productAnalytics';

import {
  AIContentGeneratorDialog,
  MessageTone,
  useAIContentGenerator
} from './index';

const ToolbarIconLabelWithIcon = styled('div')<{ newUx?: boolean }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing(0.5),
  // NEW_UX uses blue600 (#1D4ED8) so the Variable / AI-assist links read as the same
  // interactive blue as the rest of the new editor; classic keeps the app blue.
  color: p.newUx ? '#1D4ED8' : p.theme.custom.colors.primary.main
}));

const AlignCommandIcon = styled('div')<{ active?: boolean }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  color: p.active ? '#1D4ED8' : '#6B7280'
}));

export type CalloutColor = IContentCalloutTemplate['data']['color'];
const CALLOUT_COLORS: { value: CalloutColor; label: string }[] = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'gray', label: 'Gray' },
  { value: 'red', label: 'Red' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'purple', label: 'Purple' }
];

const ColorSwatch = styled('div')<{ background: string; border: string }>((p) => ({
  width: 14,
  height: 14,
  borderRadius: 2,
  background: p.background,
  border: `1.5px solid ${p.border}`
}));

const ColorSwatchButton = styled('button')`
  display: flex;
  padding: 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    background: #ebf3fe;
  }
`;

const ColorPickerRow = styled('div')`
  display: flex;
  gap: 2px;
  padding: 6px;
`;

type MarkdownEditorWithAIProps = {
  tone?: MessageTone;
  value: string;
  onChange: (nextValue: string, wasAIGenerated?: boolean) => void;
  tokens: TokenDefinition[];
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  rows?: number;
  onBlur?: (ev: React.FocusEvent<HTMLDivElement>) => void;
  fullWidth?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  // Only read in newUx: the inline canvas editor has no separate alignment control
  // (classic edits Text blocks through BlockSettingsContentText, which already has
  // its own TextAlignToggleButtonGroupWithLabel), so newUx surfaces it as toolbar
  // commands instead.
  onChangeTextAlign?: (nextAlign: 'left' | 'center' | 'right') => void;
  // Callout blocks only (Text blocks have no color) — same newUx-only rationale as
  // onChangeTextAlign above. When set, the editor's own background is tinted to
  // match the chosen color, mirroring the block's actual rendered appearance.
  calloutColor?: CalloutColor;
  onChangeCalloutColor?: (nextColor: CalloutColor) => void;
  // NEW_UX: light float-on-focus toolbar; AI moves from the big "Write it for me"
  // button into a compact toolbar command. Reskin only — same feature set.
  newUx?: boolean;
  placeholder?: string;

  // Error tracking context vars
  templateId?: string;
  templateName?: string;
  timelineItemId?: string;
  timelineItemType?: 'task' | 'message';
  timelineItemTitle?: string;
};

/**
 * Markdown editor with both replacement tokens and AI content generation
 * Combines the existing token functionality with the new AI generation feature
 */
export const MarkdownEditorWithAI = React.forwardRef<
  ContextStore,
  MarkdownEditorWithAIProps
>(
  (
    {
      tone,
      templateId,
      templateName,
      timelineItemId,
      timelineItemType,
      timelineItemTitle,
      value,
      onChange,
      tokens,
      disabled,
      newUx,
      placeholder,
      textAlign,
      onChangeTextAlign,
      calloutColor,
      onChangeCalloutColor,
      ...restProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { org } = useCurrentUser();
    const isAIEnabled = useIsOrgAIEnabled(org.id);
    const theme = useTheme();

    const [hover, setHover] = useState(false);
    const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(
      null
    );

    const { enqueueSnackbarError } = useSnackbar();

    const { trackEvent } = useProductAnalytics();

    const [aiDialogOpen, setAiDialogOpen] = useState(false);

    const [tokenDialogState, setTokenDialogState] = useState<{
      state: TextState;
      api: TextAreaTextApi;
    } | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const handleInsertText = useCallback(
      (generatedText: string) => {
        const needsNewline = value.length > 0 && !value.endsWith('\n');
        const newText = value + (needsNewline ? '\n\n' : '') + generatedText;
        onChange(newText, true);
      },
      [value, onChange]
    );

    const {
      state,
      error,
      generateAndInsert,
      resetState
    } = useAIContentGenerator(handleInsertText, {
      organizationId: org.id,
      templateId,
      timelineItemId,
      timelineItemTitle,
      timelineItemType
    });

    const handleGenerate = useCallback(
      async (prompt: string, tone: MessageTone) => {
        const success = await generateAndInsert(prompt, tone);
        if (success) {
          setAiDialogOpen(false);
          resetState();
        }
      },
      [generateAndInsert, resetState]
    );

    React.useEffect(() => {
      if (error) {
        enqueueSnackbarError(error);
        resetState();
      }
    }, [error, enqueueSnackbarError, resetState]);

    const handleCloseAiDialog = useCallback(() => {
      setAiDialogOpen(false);
      resetState();
    }, [resetState]);

    const handleCloseTokenDialog = useCallback(() => {
      if (tokenDialogState) {
        // reposition cursor and refocus
        setTimeout(() => {
          tokenDialogState.api.setSelectionRange(
            tokenDialogState.state.selection
          );
        });
      }
      setTokenDialogState(null);
    }, [tokenDialogState]);

    const openAiDialog = useCallback(() => {
      trackEvent({
        eventType: PAEventTypeV2.AI_CONTENT_GENERATOR,
        name: 'AI Text Generation Button Clicked',
        Explanation:
          'An admin clicked the button to open the content generation modal',
        'Experience Template ID': templateId,
        'Experience Template Name': templateName,
        'Timeline Item ID': timelineItemId,
        'Timeline Item Type': timelineItemType,
        'Timeline Item Title': timelineItemTitle
      });
      setAiDialogOpen(true);
    }, [
      trackEvent,
      templateId,
      templateName,
      timelineItemId,
      timelineItemType,
      timelineItemTitle
    ]);

    // Create commands for the toolbar
    const extraCommands: ICommand[] = useMemo(() => {
      const commands: ICommand[] = [];

      // Callout blocks only. Placed first (left of alignment), per request.
      if (newUx && onChangeCalloutColor) {
        const swatchColors =
          theme.custom.colors[calloutColor || 'blue'];
        commands.push({
          name: 'calloutColor',
          keyCommand: 'calloutColor',
          buttonProps: { 'aria-label': 'Callout color', disabled },
          icon: (
            <ColorSwatch background={swatchColors.w200} border={swatchColors.w500} />
          ),
          execute: () => {
            // @uiw/react-md-editor renders each command as a real <button
            // data-name="...">, so we can anchor to the actual button rather than
            // the whole editor box (execute() itself gets no DOM/event reference).
            const btn = rootRef.current?.querySelector<HTMLElement>(
              '[data-name="calloutColor"]'
            );
            setColorPickerAnchor((prev) => (prev ? null : btn || rootRef.current));
          }
        });
      }

      // NEW_UX only: classic edits Text blocks through BlockSettingsContentText,
      // which already has its own TextAlignToggleButtonGroupWithLabel below the
      // editor — newUx has no such panel (Text blocks are edited inline in the
      // canvas), so alignment lives here as toolbar commands instead. Placed first
      // (left of Text Variable / AI assist) per request.
      if (newUx && onChangeTextAlign) {
        const aligns: { value: 'left' | 'center' | 'right'; label: string; Icon: typeof AlignLeft }[] = [
          { value: 'left', label: 'Align left', Icon: AlignLeft },
          { value: 'center', label: 'Align center', Icon: AlignCenter },
          { value: 'right', label: 'Align right', Icon: AlignRight }
        ];
        aligns.forEach(({ value: alignValue, label, Icon }) => {
          commands.push({
            name: `align-${alignValue}`,
            keyCommand: `align-${alignValue}`,
            buttonProps: { 'aria-label': label, disabled },
            icon: (
              <AlignCommandIcon active={(textAlign || 'left') === alignValue}>
                <Icon size={14} />
              </AlignCommandIcon>
            ),
            execute: () => onChangeTextAlign(alignValue)
          });
        });
        commands.push(mdCommands.divider);
      }

      // Token insertion command
      commands.push({
        name: 'token',
        keyCommand: 'token',
        buttonProps: { 'aria-label': 'Insert Text Variable', disabled },
        icon: (
          <ToolbarIconLabelWithIcon newUx={newUx}>
            <div>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </div>
            <div>Text Variable</div>
          </ToolbarIconLabelWithIcon>
        ),
        execute: (state: TextState, api: TextAreaTextApi) => {
          setTokenDialogState({ state, api });
        }
      });

      // NEW_UX: the "Write it for me" affordance lives in the toolbar (matching the
      // prototype's "AI assist" chip) instead of the standalone gradient button.
      if (newUx && isAIEnabled) {
        commands.push({
          name: 'ai',
          keyCommand: 'ai',
          buttonProps: { 'aria-label': 'AI assist', disabled },
          icon: (
            <ToolbarIconLabelWithIcon newUx={newUx}>
              <img
                src="/images/ai-button-icon.svg"
                alt=""
                style={{ height: 14, width: 14 }}
              />
              <div>AI assist</div>
            </ToolbarIconLabelWithIcon>
          ),
          execute: () => openAiDialog()
        });
      }

      return commands;
    }, [
      disabled,
      isAIEnabled,
      t,
      newUx,
      openAiDialog,
      onChangeTextAlign,
      textAlign,
      onChangeCalloutColor,
      calloutColor,
      theme
    ]);

    return (
      <div
        ref={rootRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {isAIEnabled && !newUx && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8
            }}
          >
            <GradientButtonWithPromise
              disabled={disabled}
              pending="Generating..."
              isLoading={state === 'generating' || state === 'inserting'}
              startIcon={
                <img
                  src="/images/ai-button-icon.svg"
                  alt=""
                  style={{ height: 20, width: 20 }}
                />
              }
              onClick={async () => {
                openAiDialog();
              }}
            >
              Write it for me
            </GradientButtonWithPromise>
          </div>
        )}

        <div style={{ width: '100%', maxWidth: '100%' }}>
          <MarkdownEditor
            {...restProps}
            ref={ref}
            newUx={newUx}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            textAlign={textAlign}
            backgroundColor={
              calloutColor ? theme.custom.colors[calloutColor].w200 : undefined
            }
            disabled={
              disabled || state === 'generating' || state === 'inserting'
            }
            extraCommands={extraCommands}
          />
        </div>

        {onChangeCalloutColor && (
          <Popover
            open={!!colorPickerAnchor}
            anchorEl={colorPickerAnchor}
            onClose={() => setColorPickerAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <ColorPickerRow>
              {CALLOUT_COLORS.map((c) => {
                const shades = theme.custom.colors[c.value];
                return (
                  <ColorSwatchButton
                    key={c.value}
                    type="button"
                    aria-label={c.label}
                    title={c.label}
                    onClick={() => {
                      onChangeCalloutColor(c.value);
                      setColorPickerAnchor(null);
                    }}
                  >
                    <ColorSwatch background={shades.w200} border={shades.w500} />
                  </ColorSwatchButton>
                );
              })}
            </ColorPickerRow>
          </Popover>
        )}

        {/* Token picker: NEW_UX uses the compact grouped popover; classic the dialog */}
        {newUx ? (
          <TokenPickerPopover
            anchorEl={tokenDialogState ? rootRef.current : null}
            tokens={tokens}
            onClose={handleCloseTokenDialog}
            onSelect={(token) => {
              const api = tokenDialogState?.api;
              if (!api) return;
              setTokenDialogState(null);
              const tokenText = `{{ ${token.token} }}`;
              setTimeout(() => api.replaceSelection(tokenText));
            }}
          />
        ) : (
          <TokenSelectorDialog
            open={!!tokenDialogState}
            onClose={handleCloseTokenDialog}
            tokens={tokens}
            onSelect={(token, opts) => {
              if (!tokenDialogState) {
                return;
              }
              setTimeout(() => {
                setTokenDialogState(null);
                const tokenText = `{{ ${[token.token, opts]
                  .filter((x) => x)
                  .join(' | ')} }}`;
                // also focuses
                tokenDialogState.api.replaceSelection(tokenText);
              });
            }}
          />
        )}

        {/* AI generation dialog */}
        <AIContentGeneratorDialog
          templateId={templateId}
          templateName={templateName}
          timelineItemId={timelineItemId}
          timelineItemType={timelineItemType}
          timelineItemTitle={timelineItemTitle}
          open={aiDialogOpen}
          onClose={handleCloseAiDialog}
          onGenerate={handleGenerate}
          state={state}
        />
      </div>
    );
  }
);
